<div
  class="grid grid-cols-[1fr_400px] grid-rows-[minmax(0,1fr)] rounded-t-lg bg-white h-full relative overflow-hidden"
  data-testId="branching-overlay"
>
  <!--VERSION DETAILS-->
  @if (applicationVersion()) {
    <div
      class="flex flex-col order-last justify-between w-full min-h-0 border-l border-neutral-100 border-solid shrink-0 relative"
    >
      <div class="flex flex-col min-h-0 flex-1">
        <ng-container *ngTemplateOutlet="rowHeader; context: { $implicit: 'Details' }" />
        @switch (detailsView()) {
          @case ('compare') {
            <ng-container
              *ngTemplateOutlet="
                detailsSubHeader;
                context: { $implicit: 'Compare versions', styleClass: 'px-3' }
              "
            />
            <div
              class="bg-blue-50 p-4 mx-3 rounded-2xl"
              data-testId="compare-versions-panel"
            >
              <ng-container
                *ngTemplateOutlet="
                  branchInfo;
                  context: {
                    $implicit: applicationVersion(),
                    title: compareAnyTwoActive() ? 'Selected version:' : null,
                  }
                "
              />
              @if (compareAnyTwoActive()) {
                <hr class="my-3" />
                <ng-container
                  *ngTemplateOutlet="
                    branchInfo;
                    context: { $implicit: compareWithVersion(), title: 'compared with:' }
                  "
                />
              }
            </div>
          }
          @case ('commitDetails') {
            <div
              class="px-3 pb-4 flex flex-col min-h-0 flex-1"
              data-testId="commit-details-panel"
            >
              <flx-version-info
                [applicationVersion]="applicationVersion()"
                [mergedVersionState]="mergedVersionState()"
                [actions]="versionActions()"
                [showDetails]="true"
                (actionTriggered)="handleVersionAction($event)"
              >
                <ng-container ngProjectAs="[versionInfoHeader]">
                  <ng-container
                    *ngTemplateOutlet="
                      detailsSubHeader;
                      context: {
                        $implicit: 'Commit details',
                        extraTemplate: editCommitMessageButton,
                      }
                    "
                  />
                </ng-container>
              </flx-version-info>
            </div>
          }
          @default {
            <div
              class="flex flex-col gap-3.5 px-3 py-4"
              data-testId="branch-details-panel"
            >
              <flx-branch-info
                [applicationVersion]="applicationVersion()"
                [deleteBranchState]="deleteBranchState()"
                [createdFromVersion]="branchContextTargetAppVersion()"
                [actions]="versionActions()"
                (actionTriggered)="handleVersionAction($event)"
              />
              <hr class="-mx-3" />
              <flx-version-info
                [applicationVersion]="applicationVersion()"
                [mergedVersionState]="mergedVersionState()"
                [actions]="versionActions()"
                (actionTriggered)="handleVersionAction($event)"
              />
            </div>
          }
        }
        <!-- Start resources changed -->
        @if (detailsView() !== 'commitDetails') {
          <div class="flex flex-col min-h-0 flex-1 px-3 py-4">
            <flx-version-changes
              [versionResources]="$safeNavigationMigration(versionResources?.value())"
              [loading]="$safeNavigationMigration(versionResources?.isLoading())"
              [emptyMessage]="noDifferencesMessage()"
              [showCompareButton]="isCompareOptionAvailable()"
              (selectedResourceForCompare)="toggleResourceCompare($event)"
            />
          </div>
        }
        <!-- End Resources Changed -->
      </div>

      @if (
        applicationVersion().status === ApplicationStatus.MERGE_IN_PROGRESS &&
        !applicationVersion().branchArchived &&
        detailsView() !== 'commitDetails'
      ) {
        <flx-idea-helper-box
          class="px-3"
          type="warning"
        >
          Merge conflicts — conflicts must be resolved before this version is committed.
        </flx-idea-helper-box>
      }
      <div class="px-3 py-4">
        <flx-version-actions
          [actions]="versionActions()"
          [applicationVersion]="applicationVersion()"
          (actionTriggered)="handleVersionAction($event)"
        />
      </div>
    </div>

    <div class="grid grid-rows-[48px] w-full overflow-hidden relative items-baseline">
      <!-- HEADER -->

      <ng-container
        *ngTemplateOutlet="
          rowHeader;
          context: { $implicit: 'All Branches', extraTemplate: graphHeaderActions }
        "
      />

      <!-- END HEADER -->
      <div
        #gitLogScrollElement
        class="flex h-full w-full flex-col overflow-x-scroll overflow-y-auto [overflow-anchor:none]"
      >
        <div class="flex flex-col w-full grow justify-end">
          <div class="table commit-log-table w-full relative align-bottom">
            @if (!loading()) {
              <flx-gitgraph
                [showBranchLabels]="!showBranchLabelsOutsideGitGraph"
                [graphData]="history()"
                [currentVersionId]="applicationVersion().id"
                [style.z-index]="3"
                (graphContainerWidth)="graphWidth.set($event)"
                class="absolute w-full bottom-[-18px]"
              />
            }
            <div
              [style.min-width.px]="horizontalScrollableArea()"
              class="commit-log-table z-10 relative bottom-0"
              data-testId="commit-graph-container"
            >
              @for (commitLog of history(); track commitLog.id) {
                @let isVersionCompared = compareWithVersionIdQueryParam() === commitLog.id;
                @let isVersionSelected = commitLog.id === applicationVersion().id;
                @let isVersionMergeInProgress =
                  commitLog.status === ApplicationStatus.MERGE_IN_PROGRESS;
                @let isVersionArchived = commitLog.branchArchived;
                @let highlightedCommit = isVersionSelected || isVersionCompared;
                @let branchUserinfo =
                  isVersionArchived
                    ? commitLog.branchArchivedBy
                    : commitLog.contentModifiedBy || commitLog.committedBy;
                @let isBranchSelected = applicationVersion().branchName === commitLog.branchName;
                @let rowCommitMessage = rowCommitMessages()[commitLog.id];
                <div
                  #tableRow
                  [id]="commitLog.id"
                  [ngClass]="highlightedCommit ? 'bg-blue-500/10' : 'odd:bg-neutral-200/10'"
                  [class.is-compared]="isVersionCompared"
                  class="group/item relative w-full flex items-center h-9 hover:bg-neutral-400/10"
                >
                  @if (highlightedCommit) {
                    <div class="w-1 h-full bg-blue-500 sticky left-0"></div>
                  }
                  <button
                    [ngClass]="{
                      'group-hover/item:visible':
                        !isVersionSelected && !isVersionMergeInProgress && !isVersionArchived,
                    }"
                    (click)="toggleCompareWithVersion(commitLog.id)"
                    class="invisible group-[.is-compared]/item:visible flx-ds secondary sticky left-4 small"
                    flxButton
                    variant="ghost"
                    icon="left"
                    pTooltip="Compare with current version"
                    tooltipStyleClass="text-xs"
                    tooltipPosition="bottom"
                  >
                    <i
                      class="ph ph-git-diff text-xs"
                      flxIcon
                    ></i>
                  </button>
                  <button
                    [attr.data-submitMessage]="commitLog.commitMessage"
                    [attr.data-branchName]="commitLog.branchName"
                    (click)="navigateToVersion(commitLog.id)"
                    class="flex w-full h-full"
                  >
                    <div
                      #branchInfo
                      class="sticky right-4 top-1 flex items-center ml-auto px-2 gap-2 right-0"
                    >
                      @if (commitLog.buildTag) {
                        <flx-build-tag
                          [buildTagVersion]="commitLog.buildTag"
                          [attr.data-testId]="`build-number-${commitLog.buildTag}`"
                          size="xs"
                        />
                      }
                      @if (isVersionArchived) {
                        <flx-branch-state
                          [status]="commitLog.status"
                          [isArchived]="true"
                          data-testId="archived-branch-chip"
                        />
                      }
                      @if (mergedVersionState() && isVersionSelected) {
                        <flx-branch-merge-state
                          [mergedFromBranchName]="mergedVersionState().mergedFromBranchName"
                          [isMergedBranchArchived]="mergedVersionState().isMergedBranchArchived"
                          [mergeInProgress]="mergedVersionState().mergeInProgress"
                        />
                      }
                      @if (
                        (commitLog.lastVersionInBranch && showBranchLabelsOutsideGitGraph) ||
                        isVersionSelected ||
                        isVersionArchived ||
                        highlightedCommit
                      ) {
                        <flx-branch-name
                          [branchName]="commitLog.branchName"
                          [branchOrder]="isVersionArchived ? null : commitLog.branchOrder"
                          [emphasis]="
                            highlightedCommit
                              ? 'selected'
                              : isBranchSelected
                                ? 'highlighted'
                                : 'muted'
                          "
                          [truncate]="true"
                        />
                      }
                      @if (showCommitMessages() && rowCommitMessage) {
                        <span
                          #rowCommitMessageElement
                          [pTooltip]="rowCommitMessage"
                          class="commit-message truncate max-w-60 text-xs text-neutral-700"
                          tooltipStyleClass="text-xs"
                          tooltipPosition="bottom"
                          data-testId="commit-message"
                        >
                          {{ rowCommitMessage }}
                        </span>
                      }
                      @if (branchUserinfo) {
                        <span
                          [pTooltip]="branchUserinfo.firstName + ' ' + branchUserinfo.lastName"
                          class="commit-author text-neutral-900"
                          tooltipStyleClass="text-xs"
                        >
                          {{ branchUserinfo | flxUserInitials }}
                        </span>
                      }
                    </div>
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--END GIT LOG-->
    <div class="absolute right-2 top-1.5 z-10">
      <button
        [pTooltip]="closeBranchingState().message"
        [disabled]="closeBranchingState().disabled"
        (click)="closeBranchOverlayModal()"
        class="flx-ds big"
        tooltipStyleClass="text-xs"
        variant="text"
        flxButton
        icon="center"
        data-testId="modal-close-button"
      >
        <i
          class="ph ph-x"
          flxIcon
        ></i>
      </button>
    </div>
  }
</div>

<ng-template
  [flxShowModal]="showConflictsModal()"
  (escapeKeyPressed)="closeMergeConflictsModalAndRedirect()"
  panelClass="merge-conflicts-modal"
>
  <flx-merge-conflicts-modal
    (manualMergeCompleted)="onManualMergeCompleted($event)"
    (discardManualMerge)="onDiscardManualMerge($event)"
  />
</ng-template>

<ng-template
  [flxShowModal]="!!comparedResource.value()"
  (escapeKeyPressed)="toggleResourceCompare()"
  panelClass="version-compare-modal"
>
  <flx-version-compare-modal (closeModal)="toggleResourceCompare()">
    <flx-compare-versioning-info
      [compareWithVersion]="compareWithVersion()"
      [selectedAppVersion]="applicationVersion()"
    />
  </flx-version-compare-modal>
</ng-template>

<ng-template
  #rowHeader
  let-title
  let-extraTemplate="extraTemplate"
>
  <div
    class="w-full flex items-center text-neutral-900 text-xl border-b border-neutral-200 px-3 h-12"
  >
    <strong>{{ title }}</strong>
    <!-- Template-level "content projection" — ngTemplateOutlet renders nothing for undefined -->
    <ng-container *ngTemplateOutlet="extraTemplate" />
  </div>
</ng-template>

<!-- Sub-header shared by the compare / commit-details states: back button + operation name -->
<ng-template
  #detailsSubHeader
  let-title
  let-extraTemplate="extraTemplate"
  let-styleClass="styleClass"
>
  <div
    [class]="styleClass"
    class="w-full flex items-center gap-2 text-neutral-900 text-sm h-12"
  >
    <button
      (click)="exitDetailsView()"
      class="flx-ds small"
      flxButton
      variant="text"
      icon="center"
      data-testId="details-back-button"
    >
      <i
        class="ph ph-arrow-left text-xs"
        flxIcon
      ></i>
    </button>
    <strong>{{ title }}</strong>
    <span class="ml-auto flex items-center">
      <ng-container *ngTemplateOutlet="extraTemplate" />
    </span>
  </div>
</ng-template>

<ng-template #editCommitMessageButton>
  @if (editCommitMessageAction().visible) {
    <button
      [disabled]="!editCommitMessageAction().enabled"
      [attr.data-testId]="editCommitMessageAction().testId"
      [pTooltip]="editCommitMessageAction().label"
      (click)="onEditSubmitMessage()"
      class="flx-ds secondary small icon-btn"
      tooltipStyleClass="text-xs"
      tooltipPosition="bottom"
      flxButton
      icon="center"
    >
      <i
        [class]="editCommitMessageAction().icon"
        flxIcon
      ></i>
    </button>
  }
</ng-template>

<ng-template
  #branchInfo
  let-applicationVersion
  let-title="title"
>
  <div class="flex flex-col gap-y-1.5 text-xs">
    @if (title) {
      <span class="text-neutral-500">{{ title }}</span>
    }
    <div class="flex flex-row flex-wrap justify-start items-center gap-2 mb-2.5">
      <flx-branch-name
        [branchName]="applicationVersion.branchName"
        [branchOrder]="applicationVersion.branchOrder"
        emphasis="selected"
      />
      @if (applicationVersion.status; as status) {
        <flx-branch-state [status]="status" />
      } @else {
        <span class="text-xs">Not available</span>
      }
    </div>

    <div class="grid grid-cols-[auto_1fr] items-center gap-1.5 text-xs text-neutral-700">
      Edited by:
      @let commitedBy = applicationVersion.committedBy ?? applicationVersion.contentModifiedBy;
      <div class="flex gap-1 items-center">
        <span
          [pTooltip]="`${commitedBy.firstName} ${commitedBy.lastName}`"
          class="commit-author text-neutral-900"
          tooltipStyleClass="text-xs"
        >
          {{ commitedBy | flxUserInitials }}
        </span>
        {{
          applicationVersion.contentModifiedDate | flxDateFormat: 'dd MMM yyyy' : 'h:mm a' : ', '
        }}
      </div>
      Message:
      <span class="font-semibold truncate">{{ applicationVersion.commitMessage }}</span>
    </div>
  </div>
</ng-template>

<ng-template #graphHeaderActions>
  <div class="flex ml-auto items-center gap-4">
    <flx-switch
      [checked]="showArchived()"
      (checkedChange)="toggleArchived()"
      class="flx-ds leading-none"
      labelPosition="end"
      data-testId="archived-branches-switch"
    >
      <flx-label class="flx-ds">
        <span class="flex items-center gap-1">
          <i
            class="ph ph-archive"
            flxIcon
          ></i>
          Archived
        </span>
      </flx-label>
    </flx-switch>

    <flx-switch
      [checked]="showCommitMessages()"
      (checkedChange)="toggleCommitMessages($event)"
      class="flx-ds leading-none"
      labelPosition="end"
      data-testId="commit-messages-switch"
    >
      <flx-label class="flx-ds">
        <span class="flex items-center gap-1">
          <i
            class="ph ph-chat-text"
            flxIcon
          ></i>
          Messages
        </span>
      </flx-label>
    </flx-switch>

    <button
      (click)="allBranchesContextMenu.toggle($event)"
      class="flx-ds secondary small icon-btn"
      flxButton
      icon="center"
      data-testId="all-branches-menu-button"
    >
      <i
        class="ph-bold ph-dots-three-vertical"
        flxIcon
      ></i>
    </button>
  </div>
</ng-template>

<p-menu
  #allBranchesContextMenu
  [popup]="true"
  [model]="allBranchesContextMenuItems()"
  styleClass="flx-prime-menu"
  appendTo="body"
  data-testid="all-branches-context-menu"
/>
